import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Progress,
  Table,
  Tag,
  Descriptions,
  Empty,
  Typography,
  Space,
  Statistic,
  Divider,
  Segmented,
  Alert,
} from "antd";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowLeftOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ProfileOutlined,
  RiseOutlined,
  FundOutlined,
  ApiOutlined,
} from "@ant-design/icons";
import { Card, Badge, Skeleton, Button } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";
import useAccess, { MODULES } from "../../hooks/useAccess";
import dayjs from "dayjs";

const { Text, Title } = Typography;

const ACCENT = {
  blue: { bg: "rgba(61,126,245,0.10)", color: "var(--accent-blue)" },
  violet: { bg: "rgba(107,78,230,0.10)", color: "var(--accent-violet)" },
  teal: { bg: "rgba(0,184,158,0.10)", color: "var(--accent-teal)" },
  amber: { bg: "rgba(224,148,26,0.10)", color: "var(--accent-amber)" },
  rose: { bg: "rgba(232,72,96,0.10)", color: "var(--accent-rose)" },
};

const StatCard = ({ icon, label, value, suffix, accent = "blue", loading }) => {
  const { bg, color } = ACCENT[accent] ?? ACCENT.blue;
  return (
    <Card style={{ height: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--text-muted)",
              display: "block",
              marginBottom: 8,
            }}
          >
            {label}
          </Text>
          {loading ? (
            <Skeleton rows={1} />
          ) : (
            <Statistic
              value={value ?? 0}
              suffix={
                suffix ? (
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{suffix}</span>
                ) : undefined
              }
              valueStyle={{
                fontSize: 26,
                fontWeight: 700,
                color: "var(--text-primary)",
                fontFamily: "'Montserrat', sans-serif",
                lineHeight: 1.2,
              }}
            />
          )}
        </div>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
      </div>
    </Card>
  );
};

const usageColor = (pct) =>
  pct >= 90
    ? "var(--color-error)"
    : pct >= 70
    ? "var(--color-warning)"
    : "var(--color-primary)";

const CycleBar = ({ label, used, limit, resetUtc }) => {
  const pct = limit > 0 ? Math.round((used / limit) * 100) : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 4,
        }}
      >
        <Text style={{ fontSize: 12, color: "var(--text-secondary)" }}>
          {label}
        </Text>
        <Text strong style={{ fontSize: 13 }}>
          {(used ?? 0).toLocaleString()} / {(limit ?? 0).toLocaleString()}
        </Text>
      </div>
      <Progress
        percent={pct}
        strokeColor={usageColor(pct)}
        showInfo={false}
        size="small"
      />
      {resetUtc && (
        <Text style={{ fontSize: 11, color: "var(--text-muted)" }}>
          Resets {dayjs(resetUtc).format("DD MMM YYYY")} ·{" "}
          {Math.max(0, (limit ?? 0) - (used ?? 0)).toLocaleString()} left
        </Text>
      )}
    </div>
  );
};

const SubscriptionUsageCard = ({ subscription, detail }) => {
  const currentPeriod = detail?.periods?.find((p) => p.isCurrent);
  const cycle = detail?.currentCycle;

  return (
    <Card style={{ height: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div>
          <Text style={{ fontSize: 12, color: "var(--text-muted)" }}>
            #{subscription.subscriptionId}
          </Text>
          <Title level={5} style={{ margin: 0 }}>
            {subscription.package?.name || "—"}
          </Title>
        </div>
        <Space size={4}>
          <Tag>{subscription.package?.periodType || "—"}</Tag>
          <Badge variant={subscription.active ? "success" : "error"}>
            {subscription.active ? "Active" : "Inactive"}
          </Badge>
          <Badge variant={subscription.autoRenew ? "info" : "neutral"}>
            {subscription.autoRenew ? "Auto-Renew" : "Manual"}
          </Badge>
        </Space>
      </div>

      {cycle ? (
        <>
          <CycleBar
            label="This month"
            used={cycle.monthlyUsed}
            limit={cycle.monthlyLimit}
            resetUtc={cycle.monthResetUtc}
          />
          <CycleBar
            label="This week"
            used={cycle.weeklyUsed}
            limit={cycle.weeklyLimit}
            resetUtc={cycle.weekResetUtc}
          />
          <Row gutter={16} style={{ marginTop: 4 }}>
            <Col span={8}>
              <Text
                style={{
                  fontSize: 11,
                  color: "var(--text-muted)",
                  display: "block",
                }}
              >
                Monthly left
              </Text>
              <Text
                strong
                style={{
                  fontSize: 16,
                  color:
                    cycle.monthlyRemaining === 0
                      ? "var(--color-error)"
                      : "inherit",
                }}
              >
                {(cycle.monthlyRemaining ?? 0).toLocaleString()}
              </Text>
            </Col>
            <Col span={8}>
              <Text
                style={{
                  fontSize: 11,
                  color: "var(--text-muted)",
                  display: "block",
                }}
              >
                Top-Ups
              </Text>
              <Text strong style={{ fontSize: 16 }}>
                {currentPeriod?.topUps?.length || 0}
              </Text>
            </Col>
            <Col span={8}>
              <Text
                style={{
                  fontSize: 11,
                  color: "var(--text-muted)",
                  display: "block",
                }}
              >
                Valid Until
              </Text>
              <Text strong style={{ fontSize: 13 }}>
                {currentPeriod
                  ? dayjs(currentPeriod.toDate).format("DD MMM YYYY")
                  : "—"}
              </Text>
            </Col>
          </Row>
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No active period"
          style={{ margin: "16px 0" }}
        />
      )}

      {subscription.nextRenewalDate && (
        <>
          <Divider style={{ margin: "16px 0 12px" }} />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text style={{ fontSize: 12, color: "var(--text-secondary)" }}>
              <ClockCircleOutlined style={{ marginRight: 6 }} />
              Next Renewal
            </Text>
            <Text strong style={{ fontSize: 13 }}>
              {dayjs(subscription.nextRenewalDate).format("DD MMM YYYY")}
            </Text>
          </div>
        </>
      )}
    </Card>
  );
};

const ClientUsage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const companyId = Number(id);
  const { mutate } = useMutations();
  const { can } = useAccess();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  // The per-subscription breakdown starts from the subscription list, which stays behind the
  // Subscriptions module. A developer scoped to Clients alone still gets the profile and the
  // consumption chart, and an explanatory note in place of the breakdown — not a row of 403s.
  const canSubscriptions = can(MODULES.SUBSCRIPTIONS);
  const usageVisible = canSubscriptions;

  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [details, setDetails] = useState({});
  const [series, setSeries] = useState([]);
  const [seriesDays, setSeriesDays] = useState(30);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;

    const run = async () => {
      setLoading(true);

      const clientRes = await mutateRef.current("clientUsage_client", {
        method: "get",
        endpoint: "/clients",
        params: { page: 1, pageSize: 1, search: String(companyId) },
      });

      let pickedClient = null;
      const list = clientRes?.data?.data?.items || [];
      pickedClient = list.find((c) => c.companyId === companyId) || list[0] || null;

      const subsRes = canSubscriptions
        ? await mutateRef.current("clientUsage_subs", {
            method: "get",
            endpoint: "/subscription",
            params: { page: 1, pageSize: 200, companyId },
          })
        : null;
      const subs = subsRes?.data?.data?.items || [];

      const detailResults = await Promise.all(
        subs.map((s) =>
          mutateRef
            .current("clientUsage_detail_" + s.subscriptionId, {
              method: "get",
              endpoint: `/payments/detail/${s.subscriptionId}`,
            })
            .then((r) => [s.subscriptionId, r?.data?.data || null])
            .catch(() => [s.subscriptionId, null])
        )
      );

      if (cancelled) return;

      const detailMap = Object.fromEntries(detailResults);
      setClient(pickedClient);
      setSubscriptions(subs);
      setDetails(detailMap);
      setLoading(false);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [companyId, canSubscriptions]);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;

    mutateRef
      .current("clientUsage_series", {
        method: "get",
        endpoint: `/payments/usage-series/${companyId}`,
        params: { days: seriesDays },
      })
      .then((r) => {
        if (cancelled) return;
        setSeries(r?.data?.data?.points || []);
      })
      .catch(() => {
        if (!cancelled) setSeries([]);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, seriesDays]);

  const activeSubs = subscriptions.filter((s) => s.active);

  const totals = subscriptions.reduce(
    (acc, s) => {
      const d = details[s.subscriptionId];
      if (!d) return acc;
      for (const p of d.periods || []) {
        acc.tokensUsed += p.tokenUsed || 0;
        acc.tokensLimit += p.tokenLimit || 0;
        acc.spend += Number(p.amount || 0);
        for (const t of p.topUps || []) {
          acc.spend += Number(t.amount || 0);
          acc.topUps += 1;
        }
      }
      return acc;
    },
    { tokensUsed: 0, tokensLimit: 0, spend: 0, topUps: 0 }
  );

  const overallPct =
    totals.tokensLimit > 0
      ? Math.round((totals.tokensUsed / totals.tokensLimit) * 100)
      : 0;

  // Current-month cycle totals across active subscriptions (resets monthly).
  const cycleTotals = subscriptions.reduce(
    (acc, s) => {
      const c = details[s.subscriptionId]?.currentCycle;
      if (!c) return acc;
      acc.monthlyUsed += c.monthlyUsed || 0;
      acc.monthlyRemaining += c.monthlyRemaining || 0;
      return acc;
    },
    { monthlyUsed: 0, monthlyRemaining: 0 }
  );

  const allPeriods = subscriptions.flatMap((s) => {
    const d = details[s.subscriptionId];
    if (!d) return [];
    return (d.periods || []).map((p) => ({
      ...p,
      subscriptionId: s.subscriptionId,
      packageName: s.package?.name || d.packageName,
      periodType: s.package?.periodType || d.periodType,
    }));
  });

  const periodColumns = [
    {
      title: "Subscription",
      key: "sub",
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>
            {r.packageName}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            #{r.subscriptionId} · {r.periodType}
          </Text>
        </Space>
      ),
    },
    {
      title: "Period",
      key: "period",
      render: (_, r) => (
        <Space size={6}>
          <Text style={{ fontSize: 12 }}>
            {dayjs(r.fromDate).format("DD MMM YY")} –{" "}
            {dayjs(r.toDate).format("DD MMM YY")}
          </Text>
          {r.isCurrent && <Badge variant="success">Active</Badge>}
        </Space>
      ),
    },
    {
      title: "Used",
      dataIndex: "tokenUsed",
      key: "tokenUsed",
      render: (v) => (v ?? 0).toLocaleString(),
    },
    {
      title: "Limit",
      dataIndex: "tokenLimit",
      key: "tokenLimit",
      render: (v) => (v ?? 0).toLocaleString(),
    },
    {
      title: "Remaining",
      dataIndex: "tokenRemaining",
      key: "tokenRemaining",
      render: (v) => (
        <Text
          style={{
            color: v === 0 ? "var(--color-error)" : "inherit",
            fontWeight: v === 0 ? 600 : 400,
          }}
        >
          {(v ?? 0).toLocaleString()}
        </Text>
      ),
    },
    {
      title: "Usage",
      key: "usage",
      width: 180,
      render: (_, r) => {
        const pct =
          r.tokenLimit > 0 ? Math.round((r.tokenUsed / r.tokenLimit) * 100) : 0;
        return (
          <Progress percent={pct} size="small" strokeColor={usageColor(pct)} />
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (v) => `${Number(v ?? 0).toFixed(2)} EUR`,
    },
    {
      title: "Top-Ups",
      key: "topUps",
      render: (_, r) =>
        r.topUps && r.topUps.length > 0 ? (
          <Tag color="blue">{r.topUps.length}</Tag>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <Space direction="vertical" size={20} style={{ width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <Button
          variant="ghost"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/clients")}
        />
        <div style={{ flex: 1 }}>
          <Text style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Client Usage
          </Text>
          <Title level={3} style={{ margin: 0 }}>
            {client?.name || (loading ? "Loading…" : "Client")}
          </Title>
          {client?.organizationName && (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {client.organizationName}
            </Text>
          )}
        </div>
      </div>

      {client && (
        <Card padding="md">
          <Descriptions
            column={{ xs: 1, sm: 2, md: 3 }}
            size="small"
            labelStyle={{ fontWeight: 500 }}
          >
            <Descriptions.Item label="Email">
              {client.email || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {client.phone || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Fiscal Nr.">
              {client.fiscalNumber || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Country">
              {client.country || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Contact">
              {client.contactPerson || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Member Since">
              {client.createdAt
                ? dayjs(client.createdAt).format("DD MMM YYYY")
                : "—"}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {!usageVisible && (
        <Alert
          type="info"
          showIcon
          message="Per-subscription breakdown is not available to your account"
          description="Allowances, cycles and payment periods are read from the Subscriptions module, which your account has not been granted. The client profile and the consumption chart below are unaffected."
        />
      )}

      {usageVisible && (
      <>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<ProfileOutlined />}
            label="Active Subscriptions"
            value={activeSubs.length}
            accent="teal"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<ThunderboltOutlined />}
            label="Tokens Used (This Month)"
            value={cycleTotals.monthlyUsed.toLocaleString()}
            accent="blue"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<FundOutlined />}
            label="Remaining (This Month)"
            value={cycleTotals.monthlyRemaining.toLocaleString()}
            accent={cycleTotals.monthlyRemaining === 0 ? "rose" : "violet"}
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<DollarOutlined />}
            label="Total Spend"
            value={totals.spend.toFixed(2)}
            suffix="EUR"
            accent="amber"
            loading={loading}
          />
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <RiseOutlined />
            <Text strong style={{ fontSize: 14 }}>
              Overall Token Utilization
            </Text>
          </Space>
        }
        padding="md"
      >
        {loading ? (
          <Skeleton rows={2} />
        ) : totals.tokensLimit > 0 ? (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 8,
              }}
            >
              <Text type="secondary" style={{ fontSize: 12 }}>
                Across all subscriptions &amp; periods
              </Text>
              <Text strong style={{ fontSize: 16 }}>
                {overallPct}%
              </Text>
            </div>
            <Progress
              percent={overallPct}
              strokeColor={usageColor(overallPct)}
              showInfo={false}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {totals.tokensUsed.toLocaleString()} of{" "}
                {totals.tokensLimit.toLocaleString()} tokens consumed ·{" "}
                {totals.topUps} top-up{totals.topUps === 1 ? "" : "s"}
              </Text>
            </div>
          </>
        ) : (
          <Empty description="No usage data yet" />
        )}
      </Card>
      </>
      )}

      {/* Consumption series comes straight from the client, so the Clients grant is enough. */}
      <Card
        title={
          <Space>
            <FundOutlined />
            <Text strong style={{ fontSize: 14 }}>
              Token Consumption
            </Text>
          </Space>
        }
        extra={
          <Segmented
            size="small"
            value={seriesDays}
            onChange={setSeriesDays}
            options={[
              { label: "7d", value: 7 },
              { label: "30d", value: 30 },
              { label: "90d", value: 90 },
            ]}
          />
        }
        padding="md"
      >
        {loading ? (
          <Skeleton rows={4} />
        ) : series.some((p) => p.tokens > 0) ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart
              data={series.map((p) => ({
                date: dayjs(p.date).format("DD MMM"),
                tokens: p.tokens,
                cumulative: p.cumulativeTokens,
              }))}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="usageFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--accent-blue)" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="var(--accent-blue)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="cumulativeFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--accent-violet)" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="var(--accent-violet)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: "var(--text-muted)" }}
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--text-muted)" }}
                width={56}
                tickFormatter={(v) => v.toLocaleString()}
              />
              <RechartsTooltip
                formatter={(v, name) => [
                  Number(v).toLocaleString(),
                  name === "tokens" ? "Daily" : "Cumulative",
                ]}
                contentStyle={{
                  background: "var(--surface-raised, #1a1d24)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Area
                type="monotone"
                dataKey="cumulative"
                stroke="var(--accent-violet)"
                strokeWidth={1.5}
                fill="url(#cumulativeFill)"
              />
              <Area
                type="monotone"
                dataKey="tokens"
                stroke="var(--accent-blue)"
                strokeWidth={2}
                fill="url(#usageFill)"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Empty description="No consumption recorded in this window" />
        )}
      </Card>

      {usageVisible && (
      <>
      <div>
        <Title level={5} style={{ marginBottom: 12 }}>
          <ApiOutlined style={{ marginRight: 8 }} />
          Active Subscriptions
        </Title>
        {loading ? (
          <Card loading />
        ) : activeSubs.length ? (
          <Row gutter={[16, 16]}>
            {activeSubs.map((s) => (
              <Col xs={24} lg={12} xl={8} key={s.subscriptionId}>
                <SubscriptionUsageCard
                  subscription={s}
                  detail={details[s.subscriptionId]}
                />
              </Col>
            ))}
          </Row>
        ) : (
          <Card padding="md">
            <Empty description="No active subscriptions" />
          </Card>
        )}
      </div>

      <Card
        title={
          <Space>
            <CheckCircleOutlined />
            <Text strong style={{ fontSize: 14 }}>
              Payment Periods History
            </Text>
          </Space>
        }
        padding="sm"
      >
        {loading ? (
          <Skeleton rows={4} />
        ) : allPeriods.length ? (
          <Table
            columns={periodColumns}
            dataSource={allPeriods}
            rowKey={(r) => `${r.subscriptionId}-${r.paymentId}`}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            size="small"
            rowClassName={(r) => (r.isCurrent ? "row-highlight" : "")}
          />
        ) : (
          <Empty description="No payment periods found" />
        )}
      </Card>
      </>
      )}
    </Space>
  );
};

export default ClientUsage;
